import { ApiError, apiFetch } from '@/lib/apiFetch';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';

type Produkt = {
  code: string;
  name: string;
  image: string;
  kategorie: string;
  cm_per_ks: number;
  qty: Record<string, number>;
  qty_total: number;
  m_total: number;
};

type Barva = {
  barva: string;
  bg: string;
  fg: string;
  m: Record<string, number>;
  m_total: number;
  products: Produkt[];
};

type Chybi = {
  code: string;
  name: string;
  kategorie: string;
  reason: 'naplet' | 'spotreba';
  qty_total: number;
};

type Data = { terms: string[]; colors: Barva[]; missing: Chybi[] };

const DNY = ['Ne', 'Po', 'Út', 'St', 'Čt', 'Pá', 'So'];

/** „2026-10-20" → „Út 20. 10.". */
function fmtTerm(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  return `${DNY[d.getDay()] ?? ''} ${d.getDate()}. ${d.getMonth() + 1}.`;
}

/** Metry s jedním desetinným místem, prázdné = pomlčka. */
function fmtM(m: number | undefined): string {
  if (!m) return '—';
  return `${String(Math.round(m * 10) / 10).replace('.', ',')} m`;
}

/**
 * Kontrola nápletu — kolik metrů které barvy nápletu spotřebuje to, co zbývá
 * nastříhat (stejné kusy jako Plán stříhání). Spotřeba na kus se zadává
 * u kategorie v Databázi kategorií.
 */
export function KontrolaNapletuPage() {
  const [data, setData] = useState<Data | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [doTerminu, setDoTerminu] = useState('');
  const [open, setOpen] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await apiFetch<Data>('/api/vyroba/striharna/naplet'));
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : 'Nepodařilo se načíst potřebu nápletu.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Omezení „do termínu" — termíny po něm se skryjí a nepočítají do Celkem.
  const terms = useMemo(
    () => (data?.terms ?? []).filter((t) => !doTerminu || t <= doTerminu),
    [data, doTerminu],
  );
  const rows = useMemo(
    () =>
      (data?.colors ?? [])
        .map((c) => ({ ...c, total: terms.reduce((s, t) => s + (c.m[t] ?? 0), 0) }))
        .filter((c) => c.total > 0)
        .sort((a, b) => b.total - a.total),
    [data, terms],
  );

  return (
    <>
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-text">Kontrola nápletu</h1>
          <p className="text-sm text-text-secondary mt-0.5 max-w-2xl">
            Kolik nápletu které barvy potřebujeme na to, co zbývá nastříhat. Spotřebu na kus
            nastavíš u kategorie v Databázi kategorií.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-text-secondary" htmlFor="do-terminu">
            Do termínu
          </label>
          <select
            id="do-terminu"
            value={doTerminu}
            onChange={(e) => setDoTerminu(e.target.value)}
            className="rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text focus:outline-none focus:border-primary"
          >
            <option value="">Všechny</option>
            {(data?.terms ?? []).map((t) => (
              <option key={t} value={t}>
                {fmtTerm(t)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading && <p className="text-sm text-text-muted">Načítám…</p>}
      {error && !loading && (
        <div className="rounded-lg border border-danger/20 bg-danger-light p-4 mb-6 text-sm text-danger">
          {error}{' '}
          <button type="button" onClick={() => void load()} className="underline cursor-pointer">
            Zkusit znovu
          </button>
        </div>
      )}

      {data && !loading && data.missing.length > 0 && (
        <div className="rounded-lg border border-warning/40 bg-warning-light p-4 mb-6 text-sm text-text">
          <div className="font-semibold mb-1">
            Nezapočteno — {data.missing.length} produktů bez údajů
          </div>
          <ul className="space-y-0.5">
            {data.missing.map((m) => (
              <li key={m.code}>
                <span className="font-mono">{m.code}</span> {m.name}{' '}
                <span className="text-text-muted">
                  ({m.qty_total} ks) —{' '}
                  {m.reason === 'naplet'
                    ? 'produkt nemá náplet'
                    : `kategorie ${m.kategorie || '?'} nemá spotřebu nápletu`}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {data && !loading && rows.length === 0 && (
        <p className="text-sm text-text-muted">Nic ke stříhání s vyplněným nápletem.</p>
      )}

      {rows.length > 0 && !loading && (
        <div className="bg-surface rounded-xl shadow-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-text-muted">
                <th className="px-4 py-3">Náplet</th>
                {terms.map((t) => (
                  <th key={t} className="px-3 py-3 text-right whitespace-nowrap">
                    {fmtTerm(t)}
                  </th>
                ))}
                <th className="px-4 py-3 text-right">Celkem</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => {
                const isOpen = open === c.barva;
                const products = c.products
                  .map((p) => ({ ...p, sum: terms.reduce((s, t) => s + (p.qty[t] ?? 0), 0) }))
                  .filter((p) => p.sum > 0);
                return (
                  <Fragment key={c.barva}>
                    <tr className="border-b border-border-light hover:bg-surface-hover">
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => setOpen(isOpen ? null : c.barva)}
                          aria-expanded={isOpen}
                          className="inline-flex items-center gap-2 cursor-pointer"
                        >
                          {/* Barvy štítku drží Databáze nápletů — stejně jako karta ve střihárně. */}
                          <span
                            className="inline-block rounded-full px-2.5 py-0.5 text-sm font-semibold"
                            style={{ backgroundColor: c.bg, color: c.fg }}
                          >
                            {c.barva}
                          </span>
                          <span className="text-xs text-text-muted">
                            {isOpen ? '▾' : '▸'} {products.length} produktů
                          </span>
                        </button>
                      </td>
                      {terms.map((t) => (
                        <td
                          key={t}
                          className="px-3 py-3 text-right tabular-nums text-text-secondary"
                        >
                          {fmtM(c.m[t])}
                        </td>
                      ))}
                      <td className="px-4 py-3 text-right tabular-nums font-bold text-text">
                        {fmtM(c.total)}
                      </td>
                    </tr>
                    {isOpen &&
                      products.map((p) => (
                        <tr key={p.code} className="border-b border-border-light bg-bg">
                          <td className="px-4 py-2 pl-10">
                            <div className="flex items-center gap-2">
                              {p.image ? (
                                <img
                                  src={p.image}
                                  alt=""
                                  loading="lazy"
                                  className="w-8 h-8 rounded object-contain border border-border bg-surface shrink-0"
                                />
                              ) : null}
                              <div className="min-w-0">
                                <div className="text-text truncate">
                                  <span className="font-mono">{p.code}</span> {p.name}
                                </div>
                                <div className="text-xs text-text-muted">
                                  {p.kategorie} · {String(p.cm_per_ks).replace('.', ',')} cm / ks
                                </div>
                              </div>
                            </div>
                          </td>
                          {terms.map((t) => (
                            <td
                              key={t}
                              className="px-3 py-2 text-right tabular-nums text-text-muted"
                            >
                              {p.qty[t] ? `${p.qty[t]} ks` : '—'}
                            </td>
                          ))}
                          <td className="px-4 py-2 text-right tabular-nums text-text-secondary">
                            {p.sum} ks · {fmtM((p.sum * p.cm_per_ks) / 100)}
                          </td>
                        </tr>
                      ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
